import { useState } from 'react';
import {
  makeStyles, shorthands, tokens, Text, Badge, Button, Input, Textarea,
  Dropdown, Option, Field, MessageBar, MessageBarBody,
} from '@fluentui/react-components';
import { useApi } from '../api/context';
import type { Disposition, DispositionStatus } from '../api/client';

const useStyles = makeStyles({
  panel: {
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalS,
  },
  head: { display: 'flex', alignItems: 'center', columnGap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
  fields: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', columnGap: tokens.spacingHorizontalM, rowGap: tokens.spacingVerticalS },
  actions: { display: 'flex', columnGap: tokens.spacingHorizontalS, alignItems: 'center' },
  muted: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
});

const STATUS_LABEL: Record<DispositionStatus, string> = {
  'open': 'Open',
  'in-progress': 'In progress',
  'remediated': 'Remediated',
  'accepted-risk': 'Risk accepted',
  'not-applicable': 'Not applicable',
};

const STATUS_COLOUR: Record<DispositionStatus, 'danger' | 'warning' | 'success' | 'informative' | 'subtle'> = {
  'open': 'danger',
  'in-progress': 'warning',
  'remediated': 'success',
  // Deliberately not green. An accepted risk is a decision, not a fix, and
  // colouring it as success invites a reader to count it as one.
  'accepted-risk': 'informative',
  'not-applicable': 'subtle',
};

export function DispositionBadge({ disposition }: { disposition: Disposition | null }) {
  const status = disposition?.status ?? 'open';
  return (
    <Badge appearance="tint" color={STATUS_COLOUR[status]} size="small">
      {STATUS_LABEL[status]}
    </Badge>
  );
}

export interface DispositionControlProps {
  tenantId: string;
  checkId: string;
  disposition: Disposition | null;
  onSaved: (d: Disposition) => void;
}

export function DispositionControl({ tenantId, checkId, disposition, onSaved }: DispositionControlProps) {
  const styles = useStyles();
  const api = useApi();

  const [status, setStatus] = useState<DispositionStatus>(disposition?.status ?? 'open');
  const [owner, setOwner] = useState(disposition?.owner ?? '');
  const [targetDate, setTargetDate] = useState(disposition?.targetDate ?? '');
  const [note, setNote] = useState(disposition?.note ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Leaving something unfixed needs a name against it. The API enforces this
  // too; showing it here means the consultant learns why before being refused.
  const needsOwner = status === 'accepted-risk' || status === 'not-applicable';
  const blocked = needsOwner && owner.trim().length === 0;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const saved = await api.setDisposition(tenantId, checkId, {
        status,
        owner: owner.trim() || null,
        targetDate: targetDate.trim() || null,
        note: note.trim() || null,
      });
      onSaved(saved);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.panel}>
      <div className={styles.head}>
        <Text weight="semibold" size={200}>Decision</Text>
        <DispositionBadge disposition={disposition} />
        {disposition && (
          <span className={styles.muted}>
            last changed {new Date(disposition.updatedUtc).toISOString().slice(0, 16).replace('T', ' ')}
            {disposition.updatedBy ? ` by ${disposition.updatedBy}` : ''}
          </span>
        )}
      </div>

      <div className={styles.fields}>
        <Field label="Status">
          <Dropdown
            value={STATUS_LABEL[status]}
            selectedOptions={[status]}
            onOptionSelect={(_, d) => d.optionValue && setStatus(d.optionValue as DispositionStatus)}
          >
            {(Object.keys(STATUS_LABEL) as DispositionStatus[]).map((s) => (
              <Option key={s} value={s} text={STATUS_LABEL[s]}>{STATUS_LABEL[s]}</Option>
            ))}
          </Dropdown>
        </Field>

        <Field label="Owner" required={needsOwner}>
          <Input value={owner} onChange={(_, d) => setOwner(d.value)}
                 placeholder="Who is accountable" />
        </Field>

        <Field label="Target date">
          <Input type="date" value={targetDate} onChange={(_, d) => setTargetDate(d.value)} />
        </Field>
      </div>

      <Field label="Note">
        <Textarea value={note} onChange={(_, d) => setNote(d.value)} rows={2}
                  placeholder="Why this decision was taken" />
      </Field>

      {needsOwner && (
        <MessageBar intent="warning">
          <MessageBarBody>
            Recording this as {STATUS_LABEL[status].toLowerCase()} needs an owner. A decision
            to leave something unfixed should have a name against it, and the change is
            recorded with who made it and when.
          </MessageBarBody>
        </MessageBar>
      )}

      {error && (
        <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>
      )}

      <div className={styles.actions}>
        <Button appearance="primary" size="small" disabled={saving || blocked} onClick={() => void save()}>
          {saving ? 'Saving' : 'Save decision'}
        </Button>
        <span className={styles.muted}>
          Kept against the check, so it survives the next collection.
        </span>
      </div>
    </div>
  );
}
